import { Formik } from "formik";
import i18next from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Button, Input, Spacing, Stack } from "@noom/wax-component-library";

import { AddressForm, buildAddressSchema } from "@/components/forms";
import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { DEFAULT_ADDRESS } from "@/constants";
import { useGoto, useOrderScale } from "@/hooks";
import { Address } from "@/models";
import { captureException } from "@/utils/sentry";

import { BaseQuestion } from "../core";

const QUESTION_ID = "scaleShippingAddress";
const NAME_MAX_LENGTH = 100;

type ShippingAddressFormValues = {
  firstName: string;
  lastName: string;
  shippingAddress: Address;
};

declare module "@/contexts" {
  interface SurveyAnswers {
    scaleShippingAddress?: ShippingAddressFormValues;
  }
}

const shippingAddressSchema = Yup.object({
  firstName: Yup.string()
    .max(NAME_MAX_LENGTH)
    .required(() => i18next.t("form.firstName.errors.required")),
  lastName: Yup.string()
    .max(NAME_MAX_LENGTH)
    .required(() => i18next.t("form.lastName.errors.required")),
  shippingAddress: buildAddressSchema("shippingAddress"),
});

const ScaleShippingAddressQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const { t } = useTranslation();
  const goto = useGoto();
  const { orderScale, isLoading } = useOrderScale();

  const defaultInitialValues: ShippingAddressFormValues = {
    firstName: "",
    lastName: "",
    shippingAddress: DEFAULT_ADDRESS,
  };

  const onSubmit = async (values: ShippingAddressFormValues) => {
    const recipient = `${values.firstName} ${values.lastName}`.trim();
    try {
      const finalAddress = await orderScale(values.shippingAddress, recipient);
      onClickNext({ shippingAddress: finalAddress });
    } catch (error) {
      goto.error();
      captureException(error);
    }
  };

  return (
    <BaseQuestion
      hideSurveyProgressBar
      onClickBack={onClickBack}
      questionId={QUESTION_ID}
      questionText={t("scaleShippingAddress.questionText")}
    >
      <Formik
        initialValues={{
          ...defaultInitialValues,
          ...surveyAnswers[QUESTION_ID],
        }}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={shippingAddressSchema}
      >
        {({
          handleSubmit,
          values,
          getFieldProps,
          errors,
          handleChange,
          validateField,
        }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={Spacing[8]} pb={Spacing[8]}>
              <Input
                {...getFieldProps("firstName")}
                error={errors.firstName}
                label={t("form.firstName.label")}
                onChange={async (
                  e: React.ChangeEvent<HTMLInputElement>,
                ) => {
                  await handleChange(e);
                  validateField("firstName");
                }}
                placeholder={t("form.firstName.placeholder")}
              />
              <Input
                {...getFieldProps("lastName")}
                error={errors.lastName}
                label={t("form.lastName.label")}
                onChange={async (
                  e: React.ChangeEvent<HTMLInputElement>,
                ) => {
                  await handleChange(e);
                  validateField("lastName");
                }}
                placeholder={t("form.lastName.placeholder")}
              />
              <AddressForm
                formName="shippingAddress"
                countryCode={values.shippingAddress.country}
                hideCountry
              />
              <Button
                colorScheme="primary"
                isLoading={isLoading}
                disabled={isLoading}
                size="xl"
                type="submit"
              >
                {t("scaleShippingAddress.ctaText")}
              </Button>
            </Stack>
          </form>
        )}
      </Formik>
    </BaseQuestion>
  );
};

export const Q_SCALE_SHIPPING_ADDRESS: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({ surveyAnswers: { scaleOptIn, scaleEarningOptIn } }) =>
    scaleOptIn === true || scaleEarningOptIn === true,
  component: ScaleShippingAddressQuestion,
};
